/**
 * Fragrance Quiz Web Component
 * Handles quiz flow, scoring, customer creation, and cart integration
 */
class FragranceQuiz extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.totalQuestions = 5;
    this.answers = {};
    this.products = [];
    this.recommendations = [];
    this.selectedBundle = [null, null, null, null];
  }

  connectedCallback() {
    // Check if we should skip fullscreen (user closed quiz previously)
    const skipFullscreen = sessionStorage.getItem('theme:quiz-skip-fullscreen');
    if (skipFullscreen) {
      sessionStorage.removeItem('theme:quiz-skip-fullscreen');
    } else {
      // Make quiz full screen - hide header/footer
      this.enableFullScreen();
    }

    // Parse product data
    const productDataEl = this.querySelector('[data-quiz-products]');
    if (productDataEl) {
      try {
        this.products = JSON.parse(productDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse quiz products:', e);
        this.products = [];
      }
    }

    // Get settings from attributes
    this.bundlePrice = parseInt(this.dataset.bundlePrice) || 3291;
    this.bundleQuantity = parseInt(this.dataset.bundleQuantity) || 3;
    this.emailRequired = this.dataset.emailRequired === 'true';

    // Cache DOM elements
    this.screens = this.querySelectorAll('[data-screen]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.progressText = this.querySelector('[data-progress-text]');
    this.emailForm = this.querySelector('#quiz-email-form');
    this.emailInput = this.querySelector('#quiz-form-email');
    this.bundleSelects = this.querySelectorAll('[data-bundle-select]');
    this.addBundleButton = this.querySelector('[data-add-bundle]');
    this.bundleButtonText = this.querySelector('[data-bundle-button-text]');

    // Initialize event listeners
    this.initializeEventListeners();

    // Check if returning from successful form submission
    if (this.checkForFormSuccess()) {
      return; // Will show results directly
    }

    // Restore saved progress
    this.restoreProgress();

    // Show current screen
    this.showScreen(this.currentStep);
  }

  saveProgress() {
    const progressData = {
      currentStep: this.currentStep,
      answers: this.answers,
      timestamp: Date.now()
    };
    sessionStorage.setItem('theme:quiz-progress', JSON.stringify(progressData));
  }

  restoreProgress() {
    const saved = sessionStorage.getItem('theme:quiz-progress');
    if (!saved) return;

    try {
      const data = JSON.parse(saved);

      // Only restore if less than 30 minutes old
      if (data.timestamp && (Date.now() - data.timestamp) > 30 * 60 * 1000) {
        sessionStorage.removeItem('theme:quiz-progress');
        return;
      }

      // Restore state
      this.currentStep = data.currentStep || 0;
      this.answers = data.answers || {};

      // Restore visual selections
      Object.entries(this.answers).forEach(([questionId, answer]) => {
        const screen = this.querySelector(`[data-question-id="${questionId}"]`);
        if (screen) {
          const option = screen.querySelector(`[data-answer="${answer.value}"]`);
          if (option) {
            option.classList.add('quiz__option--selected');
          }
        }
      });

    } catch (e) {
      console.error('Error restoring quiz progress:', e);
      sessionStorage.removeItem('theme:quiz-progress');
    }
  }

  checkForFormSuccess() {
    // Check if we have stored quiz data and the form was just submitted
    const storedData = localStorage.getItem('theme:quiz-pending');

    if (storedData) {
      try {
        const data = JSON.parse(storedData);

        // Check if this is a fresh return (within 60 seconds of submission)
        if (data.timestamp && (Date.now() - data.timestamp) < 60000) {
          console.log('Returning from form submission, restoring quiz data');

          // Restore state
          this.answers = data.answers || {};
          this.recommendations = [];

          // Recalculate recommendations from stored answers
          this.calculateRecommendations();

          // Clear the pending data
          localStorage.removeItem('theme:quiz-pending');
          localStorage.setItem('theme:quiz-completed', 'true');

          // Show results directly
          this.showResultsAfterCalculation();
          return true;
        }
      } catch (e) {
        console.error('Error restoring quiz data:', e);
      }

      // Clear stale data
      localStorage.removeItem('theme:quiz-pending');
    }

    return false;
  }

  initializeEventListeners() {
    // Option clicks
    this.querySelectorAll('.quiz__option').forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e));
    });

    // Back buttons
    this.querySelectorAll('[data-quiz-back]').forEach(btn => {
      btn.addEventListener('click', () => this.goBack());
    });

    // Email form - use capture to set tags before submit
    if (this.emailForm) {
      this.emailForm.addEventListener('submit', (e) => this.handleEmailSubmit(e), false);
    }

    // Skip email
    const skipEmailBtn = this.querySelector('[data-quiz-skip-email]');
    if (skipEmailBtn) {
      skipEmailBtn.addEventListener('click', () => this.skipEmail());
    }

    // Bundle selects
    this.bundleSelects.forEach(select => {
      select.addEventListener('change', (e) => this.handleBundleSelect(e));
    });

    // Add bundle button
    if (this.addBundleButton) {
      this.addBundleButton.addEventListener('click', () => this.addBundleToCart());
    }

    // Retake quiz
    const retakeBtn = this.querySelector('[data-quiz-retake]');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', () => this.retakeQuiz());
    }

    // Close button
    const closeBtn = this.querySelector('[data-quiz-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeQuiz());
    }
  }

  handleOptionClick(e) {
    const option = e.currentTarget;
    const screen = option.closest('[data-screen]');
    const questionId = screen.dataset.questionId;
    const answer = option.dataset.answer;
    const tags = option.dataset.tags ? option.dataset.tags.split(',').map(t => t.trim()) : [];

    // Store answer
    this.answers[questionId] = {
      value: answer,
      tags: tags
    };

    // Visual feedback
    screen.querySelectorAll('.quiz__option').forEach(opt => {
      opt.classList.remove('quiz__option--selected');
    });
    option.classList.add('quiz__option--selected');

    // Save progress
    this.saveProgress();

    // Auto-advance after short delay
    setTimeout(() => {
      this.goNext();
    }, 300);
  }

  goNext() {
    if (this.currentStep < this.totalQuestions - 1) {
      // More questions
      this.currentStep++;
      this.saveProgress();
      this.showScreen(this.currentStep);
    } else if (this.currentStep === this.totalQuestions - 1) {
      // Calculate recommendations before showing email screen
      // So they're ready for the tags
      this.calculateRecommendations();
      console.log('Recommendations calculated:', this.recommendations.map(r => r.title));

      // Show email screen
      this.currentStep++;
      this.saveProgress();
      this.showScreen(5); // Email screen
    }
  }

  goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      // If we're on email or results screen, go back to last question
      if (this.currentStep >= this.totalQuestions) {
        this.currentStep = this.totalQuestions - 1;
      }
      this.saveProgress();
      this.showScreen(this.currentStep);
    }
  }

  showScreen(index) {
    // Hide all screens
    this.screens.forEach(screen => {
      screen.style.display = 'none';
    });

    // Show target screen
    const targetScreen = this.querySelector(`[data-screen="${index}"]`);
    if (targetScreen) {
      targetScreen.style.display = 'block';

      // Animate in
      targetScreen.classList.remove('quiz__screen--animate-in');
      void targetScreen.offsetWidth; // Force reflow
      targetScreen.classList.add('quiz__screen--animate-in');
    }

    // Scroll to quiz top
    this.scrollToQuiz();

    // Update progress bar (only for question screens)
    this.updateProgress();
  }

  scrollToQuiz() {
    // Scroll to top since quiz is full screen
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  enableFullScreen() {
    // Store references to hidden elements so we can restore them
    this.hiddenElements = [];

    // Hide header, footer, announcement bar
    const elementsToHide = [
      '#shopify-section-header',
      '#shopify-section-footer',
      '#shopify-section-announcement-bar',
      '.shopify-section--header',
      '.shopify-section--footer',
      '.shopify-section--announcement-bar',
      'header.header',
      'footer.footer',
      '.announcement-bar'
    ];

    elementsToHide.forEach(selector => {
      const el = document.querySelector(selector);
      if (el) {
        this.hiddenElements.push({ el, originalDisplay: el.style.display });
        el.style.display = 'none';
      }
    });

    // Add full screen class to body
    document.body.classList.add('quiz-fullscreen');
  }

  disableFullScreen() {
    // Restore hidden elements
    if (this.hiddenElements) {
      this.hiddenElements.forEach(({ el, originalDisplay }) => {
        el.style.display = originalDisplay || '';
      });
    }

    // Remove full screen class
    document.body.classList.remove('quiz-fullscreen');
  }

  closeQuiz() {
    // Set flag to skip fullscreen on next load
    sessionStorage.setItem('theme:quiz-skip-fullscreen', 'true');

    // Exit full screen mode
    this.disableFullScreen();

    // Navigate to scent quiz page
    window.location.href = '/pages/scent-quiz';
  }

  updateProgress() {
    if (!this.progressFill || !this.progressText) return;

    const progressEl = this.querySelector('[data-quiz-progress]');

    if (this.currentStep >= this.totalQuestions) {
      // Hide progress on email/results screens
      if (progressEl) progressEl.style.display = 'none';
      return;
    }

    if (progressEl) progressEl.style.display = 'block';

    const progress = ((this.currentStep + 1) / this.totalQuestions) * 100;
    this.progressFill.style.width = `${progress}%`;
    this.progressText.textContent = `Question ${this.currentStep + 1} of ${this.totalQuestions}`;
  }

  handleEmailSubmit(e) {
    // Klaviyo path: capture email + fire event entirely client-side. No Shopify
    // form submission and no page reload, so we stop the form and drive UI ourselves.
    e.preventDefault();

    const email = this.emailInput?.value;
    if (!email) return;

    // Make sure recommendations are scored before we ship them off
    if (this.recommendations.length === 0) {
      this.calculateRecommendations();
    }

    // Brief loading state for UX continuity; results render as soon as scoring is done
    this.showLoadingScreen();

    // Fire-and-forget so a slow Klaviyo response can't block the results screen
    this.sendToKlaviyo(email).catch(err => console.error('Klaviyo error:', err));

    setTimeout(() => this.showResultsAfterCalculation(), 600);
  }

  buildTags() {
    const tags = ['quiz-completed-3x'];

    // Add gender tag
    if (this.answers.gender) {
      tags.push(`quiz-gender-${this.answers.gender.value}`);
    }

    // Add preference tags
    const allTags = Object.values(this.answers)
      .flatMap(a => a.tags || [])
      .slice(0, 5);
    allTags.forEach(tag => {
      tags.push(`quiz-${tag}`);
    });

    // Add recommendation tags (number and name)
    this.recommendations.forEach((rec, index) => {
      if (rec && rec.title) {
        const productNumber = rec.title.split(' - ')[0].trim();
        // Extract name: "297 - Inspired by Sauvage (Male)" -> "Sauvage"
        const fullName = rec.title.split(' - ').slice(1).join(' - ').trim();
        // Extract just the fragrance name from "Inspired by X (Gender)"
        const nameMatch = fullName.match(/Inspired by ([^(]+)/i);
        const shortName = nameMatch ? nameMatch[1].trim() : fullName.split('(')[0].trim();

        tags.push(`quiz-rec-${index + 1}-${productNumber}`);
        tags.push(`quiz-rec-${index + 1}-name-${shortName.replace(/\s+/g, '-')}`);
      }
    });

    return tags;
  }

  buildKlaviyoProperties() {
    // Properties shipped to Klaviyo with the "Completed Scent Quiz" event.
    // The email template renders these via tags like {{ event.recommendations.0.name }}.
    // URLs must be absolute (https://...) because emails don't have a base href,
    // and image URLs must include the scheme - older Outlook drops protocol-relative
    // (//example.com/...) sources.
    const origin = (window.location && window.location.origin) || '';
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const toAbsoluteUrl = (img) => {
      if (!img) return '';
      if (img.startsWith('//')) return 'https:' + img;
      if (img.startsWith('http://') || img.startsWith('https://')) return img;
      if (img.startsWith('/')) return origin + img;
      return img;
    };

    const recs = this.recommendations.map((rec, index) => {
      if (!rec || !rec.title) return null;
      const productNumber = rec.title.split(' - ')[0].trim();
      const fullName = rec.title.split(' - ').slice(1).join(' - ').trim() || rec.title;
      const nameMatch = fullName.match(/Inspired by ([^(]+)/i);
      const shortName = nameMatch ? nameMatch[1].trim() : fullName.split('(')[0].trim();
      // Display title strips the trailing "(Male)/(Female)/(Unisex)" tag so
      // emails read e.g. "56 - Inspired by Le Male" rather than just "Le Male".
      const displayTitle = rec.title.split('(')[0].trim().replace(/\s*-\s*$/, '');
      return {
        position: index + 1,
        number: productNumber,
        name: displayTitle,
        short_name: shortName,
        full_title: rec.title,
        handle: rec.handle || '',
        url: rec.handle ? `${origin}${rootPath}products/${rec.handle}` : '',
        image_url: toAbsoluteUrl(rec.image),
        price: rec.price || ''
      };
    }).filter(Boolean);

    const preferences = Object.values(this.answers)
      .flatMap(a => a.tags || [])
      .slice(0, 10);

    const props = {
      quiz_variant: '3x',
      quiz_gender: this.answers.gender?.value || '',
      quiz_preferences: preferences,
      quiz_preferences_csv: preferences.join(','),
      recommendations: recs
    };

    // Flat aliases so the Klaviyo email editor can drag-and-drop named fields
    // without having to index into the array.
    recs.forEach((r, i) => {
      props[`rec_${i + 1}_number`] = r.number;
      props[`rec_${i + 1}_name`] = r.name;
      props[`rec_${i + 1}_url`] = r.url;
      props[`rec_${i + 1}_image_url`] = r.image_url;
      props[`rec_${i + 1}_handle`] = r.handle;
    });

    return props;
  }

  // --- Klaviyo Client API ---------------------------------------------------
  // PASTE YOUR VALUES BEFORE SHIPPING. Public key is the 6-character Site ID
  // from Klaviyo > Account > API Keys. List ID is the marketing list quiz
  // takers should be subscribed to (find it in Klaviyo > Audience > Lists).
  // If KLAVIYO_LIST_ID is left blank, the subscription call is skipped and only
  // the event is sent (the event still upserts a profile by email).
  static KLAVIYO_PUBLIC_KEY = 'Sa8EyW';
  static KLAVIYO_LIST_ID = 'TgjV4V';
  static KLAVIYO_API_REVISION = '2026-04-15';
  static KLAVIYO_METRIC = 'Completed Scent Quiz';

  async sendToKlaviyo(email) {
    if (!FragranceQuiz.KLAVIYO_PUBLIC_KEY || FragranceQuiz.KLAVIYO_PUBLIC_KEY === '__SET_ME__') {
      console.warn('Klaviyo public key not configured; skipping send. Set FragranceQuiz.KLAVIYO_PUBLIC_KEY in quiz.js');
      return;
    }

    const properties = this.buildKlaviyoProperties();

    const calls = [];

    // 1) Subscribe with consent so the profile is on the marketing list and the
    //    flow can email them. Skipped if no list ID was configured.
    if (FragranceQuiz.KLAVIYO_LIST_ID && FragranceQuiz.KLAVIYO_LIST_ID !== '__SET_ME__') {
      calls.push(this.klaviyoFetch('/client/subscriptions/', {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: FragranceQuiz.KLAVIYO_LIST_ID } }
          }
        }
      }));
    }

    // 2) Track the event - this is what the Klaviyo Flow listens to.
    //    The recommendations / preferences / gender ride along as event
    //    properties so the email template can render them dynamically.
    calls.push(this.klaviyoFetch('/client/events/', {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          time: new Date().toISOString(),
          metric: { data: { type: 'metric', attributes: { name: FragranceQuiz.KLAVIYO_METRIC } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    }));

    const results = await Promise.allSettled(calls);
    for (const r of results) {
      if (r.status === 'rejected') {
        console.error('Klaviyo call failed:', r.reason);
        continue;
      }
      if (r.value && !r.value.ok) {
        const body = await r.value.text().catch(() => '<unreadable>');
        console.error('Klaviyo response not ok:', r.value.status, r.value.statusText, body);
      }
    }
  }

  async klaviyoFetch(path, body) {
    const url = `https://a.klaviyo.com${path}?company_id=${FragranceQuiz.KLAVIYO_PUBLIC_KEY}`;
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'revision': FragranceQuiz.KLAVIYO_API_REVISION
      },
      body: JSON.stringify(body)
    });
  }

  showLoadingScreen() {
    // Hide all screens
    this.screens.forEach(screen => {
      screen.style.display = 'none';
    });

    // Show loading screen
    const loadingScreen = this.querySelector('[data-screen="loading"]');
    if (loadingScreen) {
      loadingScreen.style.display = 'block';
    }
  }

  skipEmail() {
    // Calculate recommendations first
    if (this.recommendations.length === 0) {
      this.calculateRecommendations();
    }

    // Show results
    this.showResultsAfterCalculation();
  }

  showResults() {
    // Calculate recommendations if not already done
    if (this.recommendations.length === 0) {
      this.calculateRecommendations();
    }
    this.showResultsAfterCalculation();
  }

  showResultsAfterCalculation() {
    // Clear saved progress since quiz is complete
    sessionStorage.removeItem('theme:quiz-progress');

    // Exit fullscreen mode so cart drawer can open
    this.disableFullScreen();

    // Show results screen
    this.currentStep = 6;
    this.showScreen(6);

    // Pre-select recommendations into the 3x bundle picker
    this.preselectBundlePicker();
  }

  preselectBundlePicker() {
    const picker = this.querySelector('bundle-picker-3x');
    if (!picker || !picker.variants || !this.recommendations.length) return;

    this.recommendations.forEach((rec, index) => {
      if (!rec) return;
      // Match quiz recommendation to picker variant by variantId
      const variant = picker.variants.find(v => v.variantId === rec.variantId);
      if (variant) {
        picker.selectVariant(index, {
          variantId: variant.variantId,
          title: variant.title,
          gender: variant.gender || ''
        });
      }
    });
  }

  /**
   * Maps abstract quiz tags to actual scent note names from the product catalogue.
   * When a user picks e.g. "Outdoors Adventure" (tags: fresh,green,citrus,aquatic),
   * we look for products whose notes contain bergamot, lemon, vetiver, etc.
   */
  static SCENT_FAMILIES = {
    // Fresh / Clean family
    fresh:   ['bergamot','lemon','grapefruit','mandarin','orange','lime','petitgrain','neroli','green','mint','peppermint','marine','lavender','tea','freesia','green tea','green notes'],
    clean:   ['musk','white musk','lavender','neroli','bergamot','lemon','freesia','lily-of-the-valley','muguet','tea','green tea','bamboo','white rose'],
    citrus:  ['bergamot','lemon','grapefruit','mandarin','orange','lime','tangerine','yuzu','clementine','blood orange','petitgrain','neroli','citrus','citron','sicilian lemon','amalfi lemon'],
    aquatic: ['marine','sea water','marine accord','marine notes','bergamot','grapefruit','mint','violet leaf','violet leaves'],
    green:   ['green','violet leaf','violet leaves','galbanum','basil','thyme','rosemary','fig','juniper','pine needles','moss','vetiver','clary sage','bamboo','green notes','green leaves'],
    light:   ['bergamot','neroli','freesia','lily-of-the-valley','muguet','musk','white musk','peony','tea','lemon','mandarin','green tea'],

    // Warm / Sweet family
    warm:    ['vanilla','amber','tonka bean','tonka','cinnamon','benzoin','caramel','honey','sandalwood','cashmeran','ambergris','myrrh','labdanum','peru balsam'],
    vanilla: ['vanilla','tonka bean','tonka','caramel','marshmallow','praline','cream','whipped cream','coconut','cocoa','chocolate','cotton candy','bourbon vanilla','vanilla bean','madagascar vanilla'],
    amber:   ['amber','ambergris','ambroxan','labdanum','benzoin','olibanum','frankincense','incense','amber gris'],
    sweet:   ['vanilla','caramel','honey','praline','marshmallow','sugar','chocolate','cocoa','tonka bean','cream','whipped cream','cotton candy','turkish delight','lychee','peach','apricot','pear','cherry','plum','raspberry','blackberry','strawberry','fig','apple','rock sugar'],
    gourmand:['vanilla','caramel','chocolate','cocoa','coffee','praline','marshmallow','honey','cream','sugar','tonka bean','almond','hazelnut','pistachio','coconut','rum','cognac','cotton candy','turkish delight','salted caramel'],

    // Woody / Earthy family
    woody:   ['cedar','cedarwood','sandalwood','vetiver','patchouli','guaiac wood','oud','birch','oak','rosewood','gaiac wood','virginia cedar','papyrus wood'],
    earthy:  ['vetiver','patchouli','oakmoss','moss','myrrh','incense','tobacco','leather','suede','birch','truffle'],
    moss:    ['oakmoss','moss','patchouli','vetiver','green'],

    // Floral family
    floral:  ['rose','jasmine','tuberose','iris','peony','lily','orchid','gardenia','magnolia','freesia','orange blossom','ylang-ylang','neroli','violet','carnation','osmanthus','heliotrope','mimosa','geranium','narcissus','wildflowers','lily of the valley','lily-of-the-valley','may rose','bulgarian rose','turkish rose','white peony','spider lily'],
    powdery: ['iris','orris','violet','heliotrope','musk','peony','lily-of-the-valley','muguet','almond','orris root'],

    // Bold / Spicy family
    bold:    ['oud','leather','saffron','pepper','black pepper','pink pepper','incense','tobacco','cinnamon','truffle','labdanum','clove','cloves','nutmeg'],
    spicy:   ['pepper','black pepper','pink pepper','cinnamon','cardamom','saffron','clove','cloves','nutmeg','ginger','cumin','coriander','chilli pepper','anise','szechuan pepper'],
    oud:     ['oud','oud wood','saffron','incense','myrrh','frankincense','olibanum','labdanum','leather','amber'],
    leather: ['leather','suede','birch','tobacco','labdanum','oud','vetiver','dark leather'],
    powerful:['oud','leather','saffron','tobacco','incense','amber','ambergris','patchouli','vetiver','labdanum','musk','pepper'],

    // Mysterious / Dark family
    mysterious:['oud','incense','saffron','myrrh','frankincense','olibanum','truffle','leather','labdanum','patchouli','amber','plum'],
    dark:    ['oud','leather','tobacco','incense','truffle','coffee','cocoa','chocolate','plum','saffron','dark woods','dark chocolate'],
    smoky:   ['incense','tobacco','oud','guaiac wood','birch','vetiver','labdanum','myrrh','leather','tar'],
    incense: ['incense','frankincense','olibanum','myrrh','labdanum','oud','saffron','benzoin'],

    // Intensity tags - these boost products with certain note density characteristics
    soft:        ['musk','white musk','peony','freesia','lily-of-the-valley','iris','neroli','bergamot','tea','green tea'],
    intimate:    ['musk','vanilla','sandalwood','amber','cashmeran','cream','iris','orris'],
    'skin-scent':['musk','sandalwood','ambroxan','cashmeran','cedarwood','iris'],
    moderate:    ['bergamot','lavender','geranium','rose','cedar','cedarwood','sandalwood','musk','vetiver','patchouli','tonka bean'],
    balanced:    ['bergamot','lavender','geranium','rose','cedar','cedarwood','sandalwood','musk','vetiver','tonka bean','vanilla'],
    versatile:   ['bergamot','lavender','cedar','cedarwood','sandalwood','musk','vetiver','geranium','rose','pepper','vanilla','ambroxan'],
    strong:      ['oud','leather','tobacco','saffron','incense','amber','patchouli','vanilla','pepper','cinnamon','clove'],
    projection:  ['oud','amber','ambergris','saffron','incense','patchouli','vanilla','tonka bean','leather','tobacco','musk','vetiver'],
    sillage:     ['oud','amber','saffron','incense','patchouli','vanilla','tonka bean','ambergris','leather','musk','labdanum','benzoin'],

    // Gender-related tags (used for Q1 soft scoring in addition to hard filter)
    masculine: ['bergamot','vetiver','leather','tobacco','oud','pepper','lavender','cedar','cedarwood','patchouli','mint','grapefruit','marine','oakmoss','incense'],
    feminine:  ['rose','jasmine','tuberose','peony','iris','freesia','gardenia','magnolia','vanilla','caramel','marshmallow','cherry','raspberry','lychee','pear','orchid','ylang-ylang'],
    unisex:    ['bergamot','sandalwood','vanilla','cedar','cedarwood','musk','ambergris','saffron','oud','patchouli','amber','vetiver','rose','incense','tonka bean','pepper']
  };

  /**
   * Count the number of individual note keywords in a product's note strings.
   */
  countNoteKeywords(product) {
    const allNotes = [
      product.topNotes || '',
      product.middleNotes || '',
      product.baseNotes || ''
    ].join(', ');

    // Split on commas and count non-empty entries
    return allNotes.split(',').filter(n => n.trim().length > 0).length;
  }

  /**
   * Score a product against the user's collected answer tags.
   * Returns a normalized number - higher = better match.
   *
   * Raw match count is divided by sqrt(noteCount) so products with
   * enormous ingredient lists (e.g. 25+ notes) don't dominate every
   * quiz path over products with focused 5-8 note profiles.
   */
  scoreProduct(product) {
    // Combine all notes into a single lowercase string for matching
    const allNotes = [
      product.topNotes || '',
      product.middleNotes || '',
      product.baseNotes || ''
    ].join(', ').toLowerCase();

    // If the product has no notes at all, give it a zero score
    if (!allNotes.trim()) return 0;

    // Collect all tags from Q2-Q5 answers (skip gender - it's used as a filter)
    const answerTags = [];
    Object.entries(this.answers).forEach(([questionId, answer]) => {
      if (questionId === 'gender') return;
      if (answer.tags && answer.tags.length) {
        answerTags.push(...answer.tags);
      }
    });

    if (answerTags.length === 0) return 0;

    let rawScore = 0;
    const matchedIngredients = new Set();

    answerTags.forEach(tag => {
      const tagLower = tag.toLowerCase().trim();
      const ingredients = FragranceQuiz.SCENT_FAMILIES[tagLower];
      if (!ingredients) return;

      ingredients.forEach(ingredient => {
        // Only count each ingredient once across all tags to avoid
        // over-weighting products that happen to match the same note
        // through multiple tag families
        if (matchedIngredients.has(ingredient)) return;

        if (allNotes.includes(ingredient.toLowerCase())) {
          matchedIngredients.add(ingredient);
          rawScore += 1;
        }
      });
    });

    // Normalize: divide by sqrt of note count so products with huge
    // note lists don't automatically win every quiz path.
    // A product with 8 notes matching 6 scores higher than one with
    // 25 notes matching 10.
    const noteCount = this.countNoteKeywords(product);
    const normalized = noteCount > 0 ? rawScore / Math.sqrt(noteCount) : 0;

    return normalized;
  }

  calculateRecommendations() {
    // 1. Filter by gender (hard filter from Q1)
    const genderAnswer = this.answers.gender?.value || 'unisex';
    const genderFiltered = this.products.filter(product => {
      return product.gender === genderAnswer;
    });

    // 2. Score each product against the user's quiz answers
    //    Add a small random jitter (+-15%) so similarly-scored products
    //    shuffle between sessions, keeping results feeling fresh/unique.
    const scored = genderFiltered.map(product => {
      const baseScore = this.scoreProduct(product);
      const jitter = 0.85 + (Math.random() * 0.30); // 0.85 - 1.15
      return {
        ...product,
        quizScore: baseScore,
        sortScore: baseScore * jitter
      };
    });

    // 3. Sort by jittered score (descending), then sales rank as tiebreaker (ascending)
    scored.sort((a, b) => {
      if (b.sortScore !== a.sortScore) return b.sortScore - a.sortScore;
      return (a.salesRank || 9999) - (b.salesRank || 9999);
    });

    // 4. Take top N
    this.recommendations = scored.slice(0, this.bundleQuantity);

    console.log('Quiz scoring results (top 10):', scored.slice(0, 10).map(p =>
      `${p.title} - score: ${p.quizScore.toFixed(2)}, jittered: ${p.sortScore.toFixed(2)}, rank: ${p.salesRank}`
    ));

    // Ensure we have enough recommendations
    if (this.recommendations.length < this.bundleQuantity) {
      const remaining = scored
        .filter(p => !this.recommendations.find(r => r.id === p.id))
        .sort((a, b) => (a.salesRank || 9999) - (b.salesRank || 9999));

      while (this.recommendations.length < this.bundleQuantity && remaining.length > 0) {
        this.recommendations.push(remaining.shift());
      }
    }
  }

  populateBundleSelects() {
    // Get gender-filtered products for dropdowns, sorted by sales rank
    const genderAnswer = this.answers.gender?.value || 'unisex';
    const availableProducts = this.products
      .filter(product => {
        return product.gender === genderAnswer;
      })
      .sort((a, b) => (a.salesRank || 9999) - (b.salesRank || 9999));

    console.log('Available products for bundle:', availableProducts.length);

    this.bundleSelects.forEach(select => {
      // Clear existing options (except placeholder)
      while (select.options.length > 1) {
        select.remove(1);
      }

      // Add products (already sorted by best sellers)
      availableProducts.forEach(product => {
        if (!product.variantId) {
          console.warn('Product missing variantId:', product.title);
          return;
        }

        const option = document.createElement('option');
        option.value = String(product.variantId); // Ensure string for select value
        option.textContent = product.title;
        option.dataset.productId = product.id;
        option.dataset.image = product.image || '';
        option.dataset.notes = [product.topNotes, product.middleNotes, product.baseNotes]
          .filter(Boolean)
          .join(', ');

        // Mark recommendations
        if (this.recommendations.find(r => r.id === product.id)) {
          option.textContent += ' ★ Recommended';
        }

        select.appendChild(option);
      });
    });
  }

  preselectRecommendations() {
    this.recommendations.forEach((rec, index) => {
      const select = this.bundleSelects[index];
      if (select && rec && rec.variantId) {
        // Convert to string for select value comparison
        const variantIdStr = String(rec.variantId);
        select.value = variantIdStr;
        this.selectedBundle[index] = rec.variantId;
        this.updatePreview(index + 1, rec);
        console.log(`Preselected slot ${index + 1}:`, rec.title, 'variantId:', rec.variantId);
      }
    });

    this.updateBundleButton();
  }

  handleBundleSelect(e) {
    const select = e.target;
    const slotIndex = parseInt(select.dataset.bundleSelect) - 1;
    const variantId = select.value;

    // Store as number for consistency
    this.selectedBundle[slotIndex] = variantId ? parseInt(variantId, 10) : null;

    // Update preview
    if (variantId) {
      const selectedOption = select.options[select.selectedIndex];
      const product = this.products.find(p => p.variantId === parseInt(variantId));
      if (product) {
        this.updatePreview(slotIndex + 1, product);
      }
    } else {
      this.clearPreview(slotIndex + 1);
    }

    this.updateBundleButton();
  }

  updatePreview(slot, product) {
    const preview = this.querySelector(`[data-recommendation-preview="${slot}"]`);
    if (!preview) return;

    const imageEl = preview.querySelector('[data-preview-image]');
    const nameEl = preview.querySelector('[data-preview-name]');
    const notesEl = preview.querySelector('[data-preview-notes]');

    if (imageEl && product.image) {
      imageEl.style.backgroundImage = `url(${product.image})`;
    }
    if (nameEl) {
      nameEl.textContent = product.title;
    }
    if (notesEl) {
      const notes = [product.topNotes, product.middleNotes, product.baseNotes]
        .filter(Boolean)
        .join(', ');
      notesEl.textContent = notes || '';
    }

    preview.classList.add('quiz__recommendation-preview--active');
  }

  clearPreview(slot) {
    const preview = this.querySelector(`[data-recommendation-preview="${slot}"]`);
    if (!preview) return;

    preview.classList.remove('quiz__recommendation-preview--active');
  }

  updateBundleButton() {
    const selectedCount = this.selectedBundle.filter(Boolean).length;
    const isComplete = selectedCount === this.bundleQuantity;

    if (this.addBundleButton) {
      this.addBundleButton.disabled = !isComplete;
    }

    if (this.bundleButtonText) {
      if (isComplete) {
        const formattedPrice = (this.bundlePrice / 100).toFixed(2);
        this.bundleButtonText.textContent = `Add Bundle to Cart - £${formattedPrice}`;
      } else {
        const remaining = this.bundleQuantity - selectedCount;
        this.bundleButtonText.textContent = `Select ${remaining} more fragrance${remaining > 1 ? 's' : ''}`;
      }
    }
  }

  async addBundleToCart() {
    if (!this.selectedBundle.every(Boolean)) return;

    const button = this.addBundleButton;
    const originalText = this.bundleButtonText?.textContent;

    // Disable and show loading
    button.disabled = true;
    if (this.bundleButtonText) {
      this.bundleButtonText.textContent = 'Adding to cart...';
    }

    try {
      // Get the root path (handle both with and without Shopify.routes)
      const rootPath = window.Shopify?.routes?.root || '/';

      // Prepare items - ensure variant IDs are valid integers
      const items = this.selectedBundle
        .filter(id => id) // Filter out null/undefined
        .map(variantId => ({
          id: typeof variantId === 'string' ? parseInt(variantId, 10) : variantId,
          quantity: 1,
          properties: {
            '_quiz_bundle': 'true'
          }
        }));

      if (items.length === 0) {
        throw new Error('No items selected');
      }

      console.log('Adding to cart:', items);

      // Prepare sections to bundle (matching Impact theme pattern)
      const sectionsToBundle = ['cart-drawer', 'cart-count'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      // Build form data with sections (theme pattern)
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        formData.append(`items[${index}][properties][_quiz_bundle]`, 'true');
      });
      formData.append('sections', sectionsToBundle.join(','));
      formData.append('sections_url', window.location.pathname);

      // Add to cart
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const responseJson = await response.json();

      if (!response.ok) {
        console.error('Cart API error:', responseJson);
        throw new Error(responseJson.description || 'Failed to add to cart');
      }

      console.log('Cart response:', responseJson);

      // Get updated cart with sections
      const cartResponse = await fetch(`${rootPath}cart.js`);
      const cartContent = await cartResponse.json();

      // Merge sections from add response into cart content
      if (responseJson.sections) {
        cartContent.sections = responseJson.sections;
      }

      // Dispatch cart events (matching theme pattern)
      document.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent
        }
      }));

      // Success feedback
      if (this.bundleButtonText) {
        this.bundleButtonText.textContent = 'Added to cart!';
      }

      // Track quiz completion with bundle
      localStorage.setItem('theme:quiz-bundle-added', 'true');

      // Reset button after delay
      setTimeout(() => {
        if (this.bundleButtonText) {
          this.bundleButtonText.textContent = originalText;
        }
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      if (this.bundleButtonText) {
        this.bundleButtonText.textContent = 'Error - Try again';
      }

      setTimeout(() => {
        if (this.bundleButtonText) {
          this.bundleButtonText.textContent = originalText;
        }
        button.disabled = false;
      }, 2000);
    }
  }

  retakeQuiz() {
    // Reset state
    this.currentStep = 0;
    this.answers = {};
    this.recommendations = [];
    this.selectedBundle = [null, null, null, null];

    // Clear saved progress
    sessionStorage.removeItem('theme:quiz-progress');

    // Clear selections
    this.querySelectorAll('.quiz__option--selected').forEach(opt => {
      opt.classList.remove('quiz__option--selected');
    });

    // Reset bundle selects
    this.bundleSelects.forEach(select => {
      select.value = '';
    });

    // Clear previews
    for (let i = 1; i <= this.bundleQuantity; i++) {
      this.clearPreview(i);
    }

    // Show first screen
    this.showScreen(0);
  }
}

// Register the custom element
if (!customElements.get('fragrance-quiz')) {
  customElements.define('fragrance-quiz', FragranceQuiz);
}


/**
 * Quiz Popup Web Component
 * Extends drawer functionality with trigger conditions
 */
class QuizPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.hasTriggered = false;
  }

  connectedCallback() {
    // Check if already completed or dismissed
    if (this.shouldSuppressPopup()) {
      this.remove();
      return;
    }

    // Get trigger settings
    this.delay = parseInt(this.getAttribute('apparition-delay')) || 0;
    this.exitIntent = this.hasAttribute('exit-intent');
    this.scrollDepth = parseInt(this.getAttribute('scroll-depth')) || 0;
    this.showOnlyOnce = this.hasAttribute('only-once');

    // Cache elements
    this.closeButtons = this.querySelectorAll('[is="close-button"], [data-close-popup]');
    this.overlay = this.querySelector('.quiz-popup__overlay');

    // Initialize event listeners
    this.initializeEventListeners();
    this.initializeTriggers();
  }

  shouldSuppressPopup() {
    if (localStorage.getItem('theme:quiz-completed')) return true;
    if (localStorage.getItem('theme:quiz-dismissed')) return true;
    if (this.showOnlyOnce && localStorage.getItem('theme:quiz-popup-shown')) return true;
    return false;
  }

  initializeEventListeners() {
    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  initializeTriggers() {
    // Time delay trigger
    if (this.delay > 0) {
      setTimeout(() => {
        if (!this.hasTriggered) {
          this.open();
        }
      }, this.delay * 1000);
    }

    // Exit intent trigger (desktop only)
    if (this.exitIntent && !this.isMobile()) {
      document.addEventListener('mouseout', (e) => {
        if (!this.hasTriggered && e.clientY < 10 && e.relatedTarget === null) {
          this.open();
        }
      });
    }

    // Scroll depth trigger
    if (this.scrollDepth > 0) {
      const checkScroll = () => {
        if (this.hasTriggered) return;

        const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercent >= this.scrollDepth) {
          this.open();
          window.removeEventListener('scroll', checkScroll);
        }
      };

      window.addEventListener('scroll', checkScroll, { passive: true });
    }

    // If no triggers, open immediately (for testing or forced show)
    if (this.delay === 0 && !this.exitIntent && this.scrollDepth === 0) {
      // Don't auto-open - wait for manual trigger
    }
  }

  isMobile() {
    return window.innerWidth < 768 || 'ontouchstart' in window;
  }

  open() {
    if (this.isOpen || this.hasTriggered) return;

    this.hasTriggered = true;
    this.isOpen = true;
    this.setAttribute('open', '');

    // Only block scroll if element is actually visible
    requestAnimationFrame(() => {
      if (this.isOpen) {
        document.body.style.overflow = 'hidden';
      }
    });

    // Mark as shown
    localStorage.setItem('theme:quiz-popup-shown', 'true');

    // Dispatch event
    this.dispatchEvent(new CustomEvent('quiz-popup:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.removeAttribute('open');
    document.body.style.overflow = '';

    // Mark as dismissed
    localStorage.setItem('theme:quiz-dismissed', 'true');

    // Dispatch event
    this.dispatchEvent(new CustomEvent('quiz-popup:close', { bubbles: true }));
  }

  // Method to force close and restore scroll (for debugging)
  forceClose() {
    this.isOpen = false;
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }
}

// Register the popup element
if (!customElements.get('quiz-popup')) {
  customElements.define('quiz-popup', QuizPopup);
}
